import { useState, useEffect } from "react";
import { Container, Form, Card, Col, Row, ProgressBar } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { motion, AnimatePresence } from "framer-motion";
import { StarFill, Star, CloudFill, Cloud, SunFill } from "react-bootstrap-icons";
import { geocodeLocation } from "./api/Mapbox";
import { calcMonthlyDaylight } from "./api/Climate";
import { sum } from "./common/Numeric";
import * as SunCalc from "suncalc";
import { toDeg } from "./common/Orbital";
import { YearMonth } from "@js-joda/core";
import { Button } from "./components/Button";

interface ScoutForm { address: string; }

interface MonthlyClimateRecord {
    month:              string;
    totalHours:         number;
    daylightHours:      number;
    elevAbove15Hours:   number | null;
    elevAbove30Hours:   number | null;
    sunshineHours:      number | null;
    cloudCoverHours:    number | null;
    precipitationHours: number | null;
    semiClearHours:     number | null;
    clearHours:         number | null;
}

interface OpenMeteoResponse {
    hourly: {
        time:                string[];
        sunshine_duration:   number[];
        cloud_cover:         number[];
        precipitation:       number[];
        shortwave_radiation: number[];
    };
}

const cls = {
    // Layout
    centerContainer:      "d-flex align-items-center justify-content-center",
    centerRow:            "justify-content-center w-100",
    colSizes:             { xs: "12" as const, md: "10" as const, lg: "6" as const },

    // Cards
    card:                 "shadow-lg rounded-5",
    cardOverflow:         "shadow-lg rounded-5 overflow-hidden",
    cardBody:             "p-5",
    resultCardBody:       "px-4 pt-4 pb-3",

    // Header
    headerRow:            "d-flex justify-content-between align-items-start",
    headerTitle:          "fs-5 fw-bold text-white",
    headerSubtitle:       "fs-6 text-white mb-1 d-flex align-items-center gap-2",
    ratingRow:            "d-flex align-items-center gap-2 mt-3",
    ratingLabel:          "text-white fw-semibold small",
    starRow:              "d-flex gap-1",

    // Legend
    legendContainer:      "d-flex flex-column gap-3 mb-3 small text-secondary",
    legendItem:           "d-flex align-items-center gap-1",

    // Month row
    monthLabel:           "text-secondary small",
    hourCount:            "fw-semibold small text-end",
    iconRow:              "d-flex gap-1",

    // Form
    formGroup:            "mb-4",
    formLabel:            "fs-5 fw-bold",
    formHint:             "small text-muted",
    inputRow:             "d-flex align-items-center",
    inputField:           "form-control-lg rounded border-primary border-3",
    submitButton:         "rounded-4 fs-5 h-100 ms-4 my-0 px-4",

    // Loading
    loadingText:          "text-center",
    loadingTitle:         "mb-3",
} as const;



const styles = {
    // Layout
    pageMinHeight:        { minHeight: "80vh" } as const,
    resultsMargin:        { className: "mt-3" },

    // Header gradient
    headerGradient: {
        background: "linear-gradient(135deg, #0d6efd 0%, #6610f2 100%)",
        padding: "24px 32px 20px",
    } as const,

    // Bar chart
    barTrack: {
        height: 12,
        borderRadius: 6,
        background: "#f0f0f0",
        overflow: "hidden" as const,
        position: "relative" as const,
    },
    barSemiClear: {
        position: "absolute" as const,
        top: 0,
        left: 0,
        height: "100%",
        borderRadius: 6,
        // background: "linear-gradient(90deg, #f5a623 0%, #f7c26b 100%)",
        background: "linear-gradient(90deg, #0d6efd 0%, #6ea8fe 100%)",
    },
    barClear: {
        position: "absolute" as const,
        top: 0,
        left: 0,
        height: "100%",
        borderRadius: 6,
        background: "linear-gradient(90deg, #f5a623 0%, #f7c26b 100%)",
        boxShadow: "0 0 6px rgba(13,110,253,0.4)",
    },

    // Month grid
    monthGrid: {
        display: "grid" as const,
        gridTemplateColumns: "auto 1fr min-content min-content min-content",
        gap: "0.5rem 0.75rem",
    },
    monthRowSubgrid: {
        display: "grid" as const,
        gridColumn: "1 / -1",
        gridTemplateColumns: "subgrid",
        alignItems: "center" as const,
    },
    headerRowSubgrid: {
        display: "grid" as const,
        gridColumn: "1 / -1",
        gridTemplateColumns: "subgrid",
        alignItems: "center" as const,
    },
    headerIcon: {
        display: "flex" as const,
        justifyContent: "center" as const,
    },
    monthLabelNoWrap:     { whiteSpace: "nowrap" as const },
    hourCountNoWrap:      { whiteSpace: "nowrap" as const },

    // Legend swatch
    legendSwatchBase: {
        width: 12,
        height: 12,
        borderRadius: 3,
        display: "inline-block" as const,
    },
    legendSwatchClear: {
        background: "linear-gradient(90deg,#f5a623,#f7c26b)",
    },
    legendSwatchSemiClear: {
        background: "linear-gradient(90deg,#0d6efd,#6ea8fe)",
    },

    // Progress bar
    progressBar:          { height: 30 } as const,
} as const;



const anim = {
    starIcon: ( delay: number ) => ( {
        initial:    { opacity: 0, scale: 0, rotate: -30 },
        animate:    { opacity: 1, scale: 1, rotate: 0 },
        transition: { delay, type: "spring" as const, stiffness: 300, damping: 14 },
    } ),
    cloudIcon: ( delay: number ) => ( {
        initial:    { opacity: 0, scale: 0, y: -6 },
        animate:    { opacity: 1, scale: 1, y: 0 },
        transition: { delay, type: "spring" as const, stiffness: 300, damping: 14 },
    } ),
    monthRow: ( delay: number ) => ( {
        initial:    { opacity: 0, x: -24 },
        animate:    { opacity: 1, x: 0 },
        transition: { delay, duration: 0.4, ease: "easeOut" as const },
    } ),
    resultsCard: {
        initial:    { opacity: 0, y: 32, scale: 0.97 },
        animate:    { opacity: 1, y: 0, scale: 1 },
        transition: { duration: 0.5, ease: "easeOut" as const },
    },
    overallStar: ( i: number ) => ( {
        initial:    { opacity: 0, scale: 0 },
        animate:    { opacity: 1, scale: 1 },
        transition: { delay: 0.3 + i * 0.1, type: "spring" as const, stiffness: 280 },
    } ),
    fadeIn: {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit:    { opacity: 0 },
    },
    barSegment: ( delay: number, widthPct: number ) => ( {
        initial:    { width: "0%" },
        animate:    { width: `${widthPct}%` },
        transition: { delay, duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number] },
    } ),
} as const;



const icons = {
    starSemiBright: { color: "#0d6efd",  },
    starFilled:     { color: "#f5a623",   },
    starEmpty:      { color: "#ccc",      },
    starLargeFill:  { color: "#f5a623",   },
    starLargeEmpty: { color: "rgba(255,255,255,0.35)" },
    cloudFilled:    { color: "#6c757d" },
    cloudEmpty:     { color: "#dee2e6" },
} as const;

function MonthRow( { record, maxClear, index }: {
    record:   MonthlyClimateRecord;
    maxClear: number;
    index:    number;
} ) {
    const [barPct, setBarPct] = useState( 0 );
    const clearHours = record.clearHours      ?? 0;
    const semiHours  = record.semiClearHours  ?? 0;
    const cloudHours = record.cloudCoverHours ?? 0;
    const clearPct   = maxClear > 0 ? ( clearHours / maxClear ) * 100 : 0;
    const semiPct    = maxClear > 0 ? ( semiHours  / maxClear ) * 100 : 0;
    const rowDelay   = index * 0.06;

    useEffect( () => {
        const t = setTimeout( () => setBarPct( 1 ), 80 + index * 60 );
        return () => clearTimeout( t );
    }, [] );

    return (
        <motion.div {...anim.monthRow( rowDelay )} style={styles.monthRowSubgrid}>
            <span className={cls.monthLabel} style={styles.monthLabelNoWrap}>
                { record.month }
            </span>

            <div style={styles.barTrack}>
                <motion.div
                    style={styles.barSemiClear}
                    {...anim.barSegment( rowDelay + 0.15, semiPct * barPct )}
                />
                <motion.div
                    style={styles.barClear}
                    {...anim.barSegment( rowDelay + 0.25, clearPct * barPct )}
                />
            </div>

            <span className={cls.hourCount} style={styles.hourCountNoWrap}>
                { clearHours.toFixed( 0 ) }h
            </span>
            <span className={cls.hourCount} style={styles.hourCountNoWrap}>
                { semiHours.toFixed( 0 ) }h
            </span>
            <span className={cls.hourCount} style={styles.hourCountNoWrap}>
                { cloudHours.toFixed( 0 ) }h
            </span>
        </motion.div>
    );
}

function ResultsCard( { results, address, onReset }: {
    results:  MonthlyClimateRecord[];
    address:  string;
    onReset:  () => void;
} ) {
    const maxClear     = Math.max( ...results.map( r => r.clearHours ?? 0 ) );
    const totalClear   = results.reduce( ( s, r ) => s + ( r.clearHours ?? 0 ), 0 );
    const maxPossible  = maxClear * 12;
    const overallScore = maxPossible > 0 ? totalClear / maxPossible : 0;
    const overallStars = Math.round( overallScore * 4 );

    return (
        <Container fluid className={`${cls.centerContainer} mt-3`}>
            <Row className={cls.centerRow}>
                <Col {...cls.colSizes}>
                    <motion.div {...anim.resultsCard}>
                        <Card className={cls.cardOverflow}>
                            {/* Header gradient strip */}
                            <div style={styles.headerGradient}>
                                <div className={cls.headerRow}>
                                    <div>
                                        <div className={cls.headerTitle}>{ address }</div>
                                        <div className={cls.headerSubtitle}>
                                            Satellite Observation Feasibility Report
                                        </div>
                                    </div>
                                    <Button variant="outline-light" size="sm" onClick={onReset}>
                                        Search
                                    </Button>
                                </div>

                                {/* Overall rating */}
                                <div className={cls.ratingRow}>
                                    <span className={cls.ratingLabel}>Annual Rating:</span>
                                    <div className={cls.starRow}>
                                        { Array.from( { length: 4 } ).map( ( _, i ) => (
                                            <motion.div key={i} {...anim.overallStar( i )}>
                                                { i < overallStars
                                                    ? <SunFill {...icons.starLargeFill}  />
                                                    : <SunFill {...icons.starLargeEmpty} /> }
                                            </motion.div>
                                        ) ) }
                                    </div>
                                </div>
                            </div>

                            <Card.Body className={cls.resultCardBody}>
                                {/* Legend */}
                                <div className={cls.legendContainer}>
                                    <div className={cls.legendItem}>
                                        <span style={{ ...styles.legendSwatchBase, ...styles.legendSwatchClear }} />
                                        Bright Hours (sun &gt;45°, cloud cover &lt;20%)
                                    </div>
                                    <div className={cls.legendItem}>
                                        <span style={{ ...styles.legendSwatchBase, ...styles.legendSwatchSemiClear }} />
                                        Mostly Bright Hours (sun &gt;30°, cloud cover &lt;20%))
                                    </div>
                                </div>

                                {/* Month rows */}
                                <div style={styles.monthGrid}>
                                    {/* Column headers */}
                                    <div style={styles.headerRowSubgrid}>
                                        <span />
                                        <span />
                                        <span style={styles.headerIcon} title="Bright hours">
                                            <SunFill {...icons.starFilled} />
                                        </span>
                                        <span style={styles.headerIcon} title="Mostly bright hours">
                                            <SunFill {...icons.starSemiBright} />
                                        </span>
                                        <span style={styles.headerIcon} title="Cloudy hours">
                                            <CloudFill {...icons.cloudFilled} />
                                        </span>
                                    </div>

                                    {results.map( ( r, i ) => (
                                        <MonthRow key={r.month} record={r} maxClear={maxClear} index={i} />
                                    ) )}
                                </div>
                            </Card.Body>
                        </Card>
                    </motion.div>
                </Col>
            </Row>
        </Container>
    );
}



export function ScoutPage() {
    const [isLoading, setIsLoading] = useState( false );
    const [progress, setProgress]   = useState( 0 );
    const [results, setResults]     = useState<MonthlyClimateRecord[]>();
    const [address, setAddress]     = useState( "" );
    const { control, handleSubmit } = useForm( { defaultValues: { address: "" } } );

    async function getWeatherData( lat: number, lng: number, year: number = 2025 ) {
        const url = `https://archive-api.open-meteo.com/v1/archive?` +
            `latitude=${lat}&longitude=${lng}` +
            `&start_date=${year}-01-01&end_date=${year}-12-31` +
            `&hourly=sunshine_duration,cloud_cover,precipitation` +
            `&precipitation_unit=inch&timezone=auto`;
        const response = await fetch( url );
        if( !response.ok ) throw new Error( "Failed to fetch climate data" );
        return await response.json();
    }

    function aggregateByMonth( lat: number, lng: number, data: OpenMeteoResponse ): MonthlyClimateRecord[] {
        const months = ["January","February","March","April","May","June",
                        "July","August","September","October","November","December"];
        const monthlyData: Record<number, {
            sunshineHours:      number[]; 
            cloudCoverHours:    number[]; 
            precipitationHours: number[];
            elevAbove15Hours:   number[]; 
            elevAbove30Hours:   number[];
            semiClearHours:     number[];
            clearHours:         number[];
        }> = {};
        for( let i = 0; i < 12; i++ ) {
            monthlyData[i] = {
                precipitationHours: [], 
                sunshineHours:      [], 
                cloudCoverHours:    [],
                elevAbove15Hours:   [], 
                elevAbove30Hours:   [], 
                semiClearHours:     [], 
                clearHours:         [],
            };
        }
        data.hourly.time.forEach( ( timeStr, i ) => {
            const date    = new Date( timeStr );
            const month   = date.getMonth();
            const sunElev = toDeg( SunCalc.getPosition( date, lat, lng ).altitude );
            if( sunElev > 15 ) monthlyData[month].elevAbove15Hours.push( 1 );
            if( sunElev > 30 ) monthlyData[month].elevAbove30Hours.push( 1 );
            if( sunElev > 15 && data.hourly.precipitation[i] > 0.001 ) monthlyData[month].precipitationHours.push( 1 );
            monthlyData[month].sunshineHours.push( ( data.hourly.sunshine_duration[i] || 0 ) / 3600 );
            if( sunElev > 15 && data.hourly.cloud_cover[i] > 20 ) monthlyData[month].cloudCoverHours.push( 1 );
            if( sunElev > 30 && data.hourly.cloud_cover[i] < 20 ) monthlyData[month].semiClearHours.push( 1 );
            if( sunElev > 45 && data.hourly.cloud_cover[i] < 20 ) monthlyData[month].clearHours.push( 1 );
        } );
        return Array.from( { length: 12 }, ( _, i ) => ( {
            month:              months[i],
            totalHours:         YearMonth.of( 2025, i + 1 ).lengthOfMonth() * 24,
            daylightHours:      calcMonthlyDaylight( lat, lng, i + 1 ),
            elevAbove15Hours:   sum( monthlyData[i].elevAbove15Hours   ),
            elevAbove30Hours:   sum( monthlyData[i].elevAbove30Hours   ),
            precipitationHours: sum( monthlyData[i].precipitationHours ),
            sunshineHours:      sum( monthlyData[i].sunshineHours      ),
            cloudCoverHours:    sum( monthlyData[i].cloudCoverHours    ),
            semiClearHours:     sum( monthlyData[i].semiClearHours     ),
            clearHours:         sum( monthlyData[i].clearHours         ),
        } ) );
    }

    const onSubmit = async ( data: ScoutForm ) => {
        setIsLoading( true );
        setProgress( 0 );
        setResults( undefined );
        setAddress( data.address );
        try {
            const loc = await geocodeLocation( data.address );
            setProgress( 20 );
            if( !loc ) throw new Error( "Could not find location" );
            const weather = await getWeatherData( loc.lat, loc.lng );
            setProgress( 60 );
            const climateInfo = aggregateByMonth( loc.lat, loc.lng, weather );
            setProgress( 100 );
            setTimeout( () => { setResults( climateInfo ); setIsLoading( false ); }, 500 );
        } catch( error ) {
            console.error( "Scouting failed:", error );
            alert( `Error: ${error instanceof Error ? error.message : "Unknown error"}` );
            setIsLoading( false );
            setProgress( 0 );
        }
    };

    if( results ) {
        return <ResultsCard results={results} address={address} onReset={() => setResults( undefined )} />;
    }

    return (
        <Container fluid className={cls.centerContainer} style={styles.pageMinHeight}>
            <Row className={cls.centerRow}>
                <Col {...cls.colSizes}>
                    <Card className={cls.card}>
                        <div className="overflow-hidden">
                            <Card.Body className={cls.cardBody}>
                                <AnimatePresence mode="wait">
                                    { isLoading ? (
                                        <motion.div key="loading" {...anim.fadeIn} className={cls.loadingText}>
                                            <h4 className={cls.loadingTitle}>Scouting Location…</h4>
                                            <ProgressBar now={progress} animated striped variant="primary" style={styles.progressBar} />
                                        </motion.div>
                                    ) : (
                                        <motion.div key="form" {...anim.fadeIn}>
                                            <Form onSubmit={handleSubmit( onSubmit )}>
                                                <Form.Group className={cls.formGroup}>
                                                    <Form.Label className={cls.formLabel}>Scout Location</Form.Label>
                                                    <p className={cls.formHint}>
                                                        Evaluates your location's suitability for satellite observation
                                                        using historical sunlight and weather data.
                                                    </p>
                                                    <div className={cls.inputRow}>
                                                        <Controller
                                                            control={control}
                                                            name="address"
                                                            rules={{ required: "Address is required" }}
                                                            render={( { field } ) => (
                                                                <Form.Control
                                                                    type="text"
                                                                    placeholder="Enter City Name"
                                                                    className={cls.inputField}
                                                                    {...field} />
                                                            )}
                                                        />
                                                        <Button variant="primary"
                                                            className={cls.submitButton} type="submit">
                                                            Search
                                                        </Button>
                                                    </div>
                                                </Form.Group>
                                            </Form>
                                        </motion.div>
                                    ) }
                                </AnimatePresence>
                            </Card.Body>
                        </div>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}